import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../Common/Modal';
import { FiInfo } from 'react-icons/fi';
import api from '../../services/api';
import actionItemService from '../../services/actionItemService';
import { useProject } from '../../context/ProjectContext';
import { formatVisitId } from '../../utils/constants';
import { actionItems as actionItemMessages } from '../../utils/validationMessages';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import './CreateActionItemModal.css';

function getLocalDateInputYmd() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

/** True when master row is Others (description or <c>nIssueCategoryMstNo</c> 10). */
function isOthersIssueCategorySelection(selectedDescription, options) {
  const d = (selectedDescription || '').toString().trim();
  if (!d) return false;
  if (d.toLowerCase() === 'others') return true;
  const opt = (options || []).find(
    (o) => (o.vIssueCategoryDescription ?? o.VIssueCategoryDescription ?? '').toString().trim() === d
  );
  return Number(opt?.nIssueCategoryMstNo ?? opt?.NIssueCategoryMstNo ?? 0) === 10;
}

/** ProfileNo string for CRA from GET /action-items/assignable-profiles (fixed assignee for new issues). */
function getCraProfileNoFromAssignableProfiles(profiles) {
  if (!Array.isArray(profiles) || profiles.length === 0) return '';
  const cra = profiles.find((p) => {
    const code = (p.profileCode ?? p.ProfileCode ?? '').toString().trim().toUpperCase();
    return code === 'CRA';
  });
  const raw = cra?.profileNo ?? cra?.nProfileNo;
  if (raw == null || raw === '') return '';
  return String(raw);
}

const CreateActionItemModal = ({
  isOpen,
  onClose,
  onSave,
  defaultProject = null,
  defaultSiteId = '',
  defaultSiteName = '',
  defaultVisitId = '',
  defaultVisitType = '',
  defaultAssignActionItemTo = '',
  defaultSectionAttributeIds = [],
  defaultNCRFDtlNo = null,
  defaultFieldCode = null,
  openPayloadRef = null,
  openedFromCRF = false,
  stageVisits = [],
  visitIdsWithCRFDraft = [],
  pi = 'Dr. Tapan Shah',
  visitReportViewOnly = false,
}) => {
  const { currentProject: contextProject } = useProject();
  const currentProject = defaultProject || (contextProject && contextProject !== 'all' ? contextProject : null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [referenceNo, setReferenceNo] = useState('');
  const [crfOptionsList, setCrfOptionsList] = useState([]);
  const [crfOptionsLoading, setCrfOptionsLoading] = useState(false);
  const [assignedToProfiles, setAssignedToProfiles] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [issueCategoryOptions, setIssueCategoryOptions] = useState([]);

  const [formData, setFormData] = useState(() => {
    const ymd = getLocalDateInputYmd();
    return {
      projectCode: currentProject || '',
      siteID: '',
      visitId: '',
      visitType: '',
      assignActionItemTo: '',
      selectedSubgroupIds: [],
      actionItemDescription: '',
      assignTo: '',
      assignedTo: '',
      dueDate: '',
      dateOfAwareness: ymd,
      dateOfIssue: ymd,
      issueCategory: '',
      issueCategoryOther: '',
      nPriorityMstNo: '',
    };
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);
  // Snapshot CRF defaults when modal opens so load (async) always applies the values from open time, not stale closure
  const crfDefaultsRef = useRef(null);
  // Keep latest section-attribute selection so payload always has it at submit (avoids stale formData)
  const selectedSubgroupIdsRef = useRef([]);
  useEffect(() => {
    if (isOpen && openedFromCRF) {
      crfDefaultsRef.current = {
        projectCode: (defaultProject != null && String(defaultProject).trim() !== '') ? String(defaultProject).trim() : null,
        siteId: defaultSiteId != null && defaultSiteId !== '' ? String(defaultSiteId) : '',
        siteName: defaultSiteName != null ? String(defaultSiteName).trim() : '',
        visitId: defaultVisitId != null && defaultVisitId !== '' ? String(defaultVisitId) : '',
        visitType: defaultVisitType != null ? String(defaultVisitType).trim() : '',
        assignActionItemTo: defaultAssignActionItemTo != null ? String(defaultAssignActionItemTo).trim() : '',
        sectionAttributeIds: Array.isArray(defaultSectionAttributeIds) ? defaultSectionAttributeIds.map((id) => String(id)) : [],
        nCRFDtlNo: defaultNCRFDtlNo != null && defaultNCRFDtlNo > 0 ? Number(defaultNCRFDtlNo) : null,
        fieldCode: defaultFieldCode != null && String(defaultFieldCode).trim() !== '' ? String(defaultFieldCode).trim() : null,
      };
    } else {
      crfDefaultsRef.current = null;
    }
  }, [isOpen, openedFromCRF, defaultProject, defaultSiteId, defaultSiteName, defaultVisitId, defaultVisitType, defaultAssignActionItemTo, defaultSectionAttributeIds, defaultNCRFDtlNo, defaultFieldCode]);

  useEffect(() => {
    if (!isOpen) {
      selectedSubgroupIdsRef.current = [];
      const todayStr = getLocalDateInputYmd();
      setFormData({
        projectCode: currentProject || '',
        siteID: '',
        visitId: '',
        visitType: '',
        assignActionItemTo: '',
        selectedSubgroupIds: [],
        actionItemDescription: '',
        assignTo: '',
        assignedTo: '',
        dueDate: todayStr,
        dateOfAwareness: todayStr,
        dateOfIssue: todayStr,
        issueCategory: '',
        issueCategoryOther: '',
        nPriorityMstNo: '',
      });
      setFieldErrors({});
      setSaveError(null);
    }
  }, [isOpen, currentProject]);

  // When opened from CRF, set Visit Type, Visit, Assign Action Item To immediately (before async load)
  useEffect(() => {
    if (!isOpen || !openedFromCRF) return;
    const d = crfDefaultsRef.current;
    if (!d) return;
    setFormData((prev) => ({
      ...prev,
      visitType: d.visitType || prev.visitType,
      visitId: d.visitId || prev.visitId,
      assignActionItemTo: d.assignActionItemTo || prev.assignActionItemTo,
    }));
  }, [isOpen, openedFromCRF]);

  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      const crfDefaults = crfDefaultsRef.current;

      try {
        const projectCode = currentProject || '';

        const projectsRes = await api.get('/ImportData/GetProject');
        const projectsList = (projectsRes.data || [])
          .filter((p) => p.isActive !== false && p.IsActive !== false)
          .map((p) => ({
            projectCode: p.projectCode || p.ProjectCode || '',
            projectDescription: p.projectDescription || p.ProjectDescription || '',
          }))
          .filter((p) => p.projectCode)
          .sort((a, b) => (a.projectCode || '').localeCompare(b.projectCode || ''));

        const refProjectCode = (openedFromCRF && crfDefaults?.projectCode)
          ? String(crfDefaults.projectCode).trim()
          : (projectCode || projectsList[0]?.projectCode || '').trim();

        const [sitesRes, refRes, profilesRes, prioritiesRes, issueCategoriesRes] = await Promise.all([
          refProjectCode ? api.get(`/sites?project=${encodeURIComponent(refProjectCode)}`) : Promise.resolve({ data: [] }),
          refProjectCode
            ? api.get('/action-items/next-reference', { params: { projectCode: refProjectCode } })
            : Promise.resolve({ data: { referenceNo: '' } }),
          api.get('/action-items/assignable-profiles').catch(() => ({ data: [] })),
          actionItemService.getPriorities().then((d) => ({ data: d })).catch(() => ({ data: [] })),
          actionItemService.getIssueCategories().then((d) => ({ data: d })).catch(() => ({ data: [] })),
        ]);

        const sitesList = (sitesRes.data || []).map((s) => {
          const siteCode = (s.siteCode || s.vSiteCode || '').toString().trim();
          const siteDescription = (s.sitedescription || s.siteDescription || s.SiteDescription || '').toString().trim();
          const siteDisplay = [siteCode, siteDescription].filter(Boolean).join(' - ') || siteCode || siteDescription || String(s.siteID ?? s.siteId ?? '');
          return {
            siteID: s.siteID ?? s.siteId,
            siteName: siteDisplay,
          };
        }).filter((s) => s.siteName);

        const profilesList = Array.isArray(profilesRes.data) ? profilesRes.data : [];
        const craProfileNo = getCraProfileNoFromAssignableProfiles(profilesList);

        setProjects(projectsList);
        setSites(sitesList);
        setReferenceNo(refRes.data?.referenceNo || (refProjectCode ? 'AI-00001' : ''));
        if (!openedFromCRF) setCrfOptionsList([]);
        setAssignedToProfiles(profilesList);
        setPriorityOptions(Array.isArray(prioritiesRes.data) ? prioritiesRes.data : []);
        setIssueCategoryOptions(Array.isArray(issueCategoriesRes.data) ? issueCategoriesRes.data : []);

        const projCode = projectCode || (projectsList[0]?.projectCode || '');
        let siteId = '';
        if (openedFromCRF && crfDefaults) {
          const wantId = crfDefaults.siteId;
          const wantName = crfDefaults.siteName;
          if (sitesList.length > 0) {
            if (wantId && sitesList.some((s) => String(s.siteID ?? s.siteId) === wantId)) {
              siteId = wantId;
            } else if (wantName) {
              const byName = sitesList.find((s) => (s.siteName || '').trim() === wantName || (s.siteName || '').toLowerCase() === wantName.toLowerCase());
              if (byName) siteId = String(byName.siteID ?? byName.siteId ?? '');
            }
            if (!siteId && wantId) siteId = wantId;
          }
          if (!siteId) siteId = sitesList[0] ? String(sitesList[0].siteID) : '';
        } else {
          siteId = (defaultSiteId != null && defaultSiteId !== '') ? String(defaultSiteId) : '';
        }

        const todayStr = getLocalDateInputYmd();
        setFormData((prev) => ({
          ...prev,
          projectCode: projCode,
          siteID: siteId,
          visitType: (openedFromCRF && crfDefaults?.visitType) ? crfDefaults.visitType : prev.visitType,
          visitId: (openedFromCRF && crfDefaults?.visitId) ? crfDefaults.visitId : prev.visitId,
          assignActionItemTo: (openedFromCRF && crfDefaults?.assignActionItemTo) ? crfDefaults.assignActionItemTo : prev.assignActionItemTo,
          selectedSubgroupIds: openedFromCRF ? (prev.selectedSubgroupIds || []) : [],
          assignedTo: craProfileNo || prev.assignedTo,
          dueDate: (prev.dueDate || '').trim() || todayStr,
          dateOfAwareness: (prev.dateOfAwareness || '').trim() || todayStr,
          dateOfIssue: todayStr,
        }));
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'Failed to load form data';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isOpen, currentProject, defaultProject, defaultSiteId, openedFromCRF]);

  const projectCode = formData.projectCode || currentProject || '';
  const siteId = formData.siteID ? String(formData.siteID) : '';
  const visitType = (formData.visitType || '').trim();
  const visitId = (formData.visitId || '').trim();

  const draftSet = Array.isArray(visitIdsWithCRFDraft) && visitIdsWithCRFDraft.length > 0
    ? new Set(visitIdsWithCRFDraft.map(Number))
    : null;
  const visitOptions = (Array.isArray(stageVisits) ? stageVisits : [])
    .filter((v) => {
      const vid = v?.scheduledVisitID ?? v?.scheduledVisitId ?? v?.ScheduledVisitID;
      if (vid == null) return false;
      // Visits are per-site: do not list any until a site is chosen (avoids showing all stage visits on empty Site).
      if (!siteId) return false;
      if (draftSet != null && !draftSet.has(Number(vid))) return false;
      const vSite = v?.siteID ?? v?.siteId;
      if (vSite == null || String(vSite) !== siteId) return false;
      if (visitType && (v.visitType ?? v.VisitType) !== visitType) return false;
      return true;
    })
    .map((v) => {
      const vid = v?.scheduledVisitID ?? v?.scheduledVisitId ?? v?.ScheduledVisitID;
      const visitDisplay = formatVisitId(v);
      const label = `${visitDisplay} - ${v?.siteName ?? v?.siteCode ?? v?.siteID ?? vid}`;
      return { value: String(vid), label };
    });

  // When opened from CRF row with nCRFDtlNo but no defaultFieldCode: fallback to section-attribute-by-crf-detail.
  // When we have defaultFieldCode we use section-attributes-for-crf in the effect below.
  useEffect(() => {
    if (!isOpen || !openedFromCRF) return;
    const hasFieldCode = (defaultFieldCode != null && String(defaultFieldCode).trim() !== '');
    if (hasFieldCode) return;
    const nCRFDtlNo = crfDefaultsRef.current?.nCRFDtlNo ?? defaultNCRFDtlNo;
    if (nCRFDtlNo == null || nCRFDtlNo <= 0) return;

    let cancelled = false;
    setCrfOptionsLoading(true);
    setCrfOptionsList([]);
    api.get('/action-items/section-attribute-by-crf-detail', { params: { nCRFDtlNo } })
      .then((res) => {
        if (cancelled) return;
        const item = res.data;
        if (!item || (item.id == null && item.Id == null)) {
          setCrfOptionsList([]);
          return;
        }
        const id = item.id ?? item.Id ?? item.code ?? item.Code;
        const label = item.displayText ?? item.DisplayText ?? item.description ?? item.Description ?? (id != null ? String(id) : '');
        const normalized = { id: String(id), code: String(id), displayText: label, description: label };
        setCrfOptionsList([normalized]);
        const singleId = String(id);
        selectedSubgroupIdsRef.current = [singleId];
        setFormData((prev) => ({ ...prev, selectedSubgroupIds: [singleId] }));
      })
      .catch(() => {
        if (!cancelled) setCrfOptionsList([]);
      })
      .finally(() => {
        if (!cancelled) setCrfOptionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, openedFromCRF, defaultNCRFDtlNo, defaultFieldCode]);

  // --- Scenario 1: opened from inside CRF Form ---
  // Use section-attributes-for-crf; run as soon as modal opens. Bind vMedExCode from defaultFieldCode when present; else from openPayloadRef (set synchronously on open to avoid state delay); else resolve from defaultNCRFDtlNo via section-attribute-by-crf-detail (id = "SubGroup_FieldCode" -> extract FieldCode).
  useEffect(() => {
    if (!isOpen || !openedFromCRF) return;
    const projCode = (defaultProject != null && String(defaultProject).trim() !== '') ? String(defaultProject).trim() : (projectCode || currentProject || '');
    const vType = (defaultVisitType != null && String(defaultVisitType).trim() !== '') ? String(defaultVisitType).trim() : (visitType || '');
    if (!projCode || !vType) return;

    const payload = openPayloadRef?.current;
    const fieldCodeFromRef = (payload?.fieldCode != null && String(payload.fieldCode).trim() !== '') ? String(payload.fieldCode).trim() : null;
    const nCRFDtlNoFromRef = payload?.nCRFDtlNo != null && payload?.nCRFDtlNo > 0 ? Number(payload.nCRFDtlNo) : null;

    let cancelled = false;
    const applyCrfSectionAttributeList = (rawList, vMedExCodeForMatch) => {
      const list = rawList.map((item) => {
        const id = item.id ?? item.Id ?? item.code ?? item.Code;
        const label = item.displayText ?? item.DisplayText ?? item.description ?? item.Description ?? item.code ?? item.Code ?? (id != null ? String(id) : '');
        return { id: id != null ? String(id) : '', code: id != null ? String(id) : '', displayText: label, description: label };
      }).filter((item) => item.id !== '');
      setCrfOptionsList(list);
      if (list.length > 0 && vMedExCodeForMatch) {
        const fc = String(vMedExCodeForMatch).trim();
        const singleId = list.length === 1 ? list[0].id : (list.find((o) => (o.id || '').endsWith('_' + fc) || (o.id || '') === fc)?.id);
        if (singleId) {
          selectedSubgroupIdsRef.current = [singleId];
          setFormData((prev) => ({ ...prev, selectedSubgroupIds: [singleId] }));
        }
      }
    };

    const loadSectionAttributeByCrfDetail = (nCRFDtlNo) => {
      if (nCRFDtlNo == null || nCRFDtlNo <= 0 || cancelled) return Promise.resolve();
      return api.get('/action-items/section-attribute-by-crf-detail', { params: { nCRFDtlNo } })
        .then((res) => {
          if (cancelled) return;
          const item = res.data;
          if (!item || (item.id == null && item.Id == null)) {
            setCrfOptionsList([]);
            return;
          }
          applyCrfSectionAttributeList([item], null);
          const id = item.id ?? item.Id ?? item.code ?? item.Code;
          if (id != null) {
            const singleId = String(id);
            selectedSubgroupIdsRef.current = [singleId];
            setFormData((prev) => ({ ...prev, selectedSubgroupIds: [singleId] }));
          }
        });
    };

    const runSectionAttributesForCrf = (vMedExCode) => {
      if (!vMedExCode || cancelled) return;
      setCrfOptionsLoading(true);
      setCrfOptionsList([]);
      const params = { projectCode: projCode, visitType: vType, vMedExCode: String(vMedExCode).trim() };
      const sId = (defaultSiteId != null && String(defaultSiteId).trim() !== '') ? String(defaultSiteId).trim() : siteId;
      if (sId) params.siteId = sId;
      const vId = (defaultVisitId != null && String(defaultVisitId).trim() !== '') ? String(defaultVisitId).trim() : visitId;
      if (vId) params.scheduledVisitId = vId;
      const nCRFDtlNoForFallback =
        (defaultNCRFDtlNo != null && defaultNCRFDtlNo > 0 ? Number(defaultNCRFDtlNo) : null) || nCRFDtlNoFromRef;
      api.get('/action-items/section-attributes-for-crf', { params })
        .then((res) => {
          if (cancelled) return;
          const rawList = Array.isArray(res.data) ? res.data : [];
          if (rawList.length === 0 && nCRFDtlNoForFallback != null) {
            return loadSectionAttributeByCrfDetail(nCRFDtlNoForFallback);
          }
          applyCrfSectionAttributeList(rawList, vMedExCode);
        })
        .catch(() => {
          if (cancelled) return;
          if (nCRFDtlNoForFallback != null) {
            return loadSectionAttributeByCrfDetail(nCRFDtlNoForFallback).catch(() => { if (!cancelled) setCrfOptionsList([]); });
          }
          setCrfOptionsList([]);
        })
        .finally(() => { if (!cancelled) setCrfOptionsLoading(false); });
    };

    const fieldCodeFromProps = (defaultFieldCode != null && String(defaultFieldCode).trim() !== '') ? String(defaultFieldCode).trim() : null;
    const fieldCodeForCrf = fieldCodeFromProps || fieldCodeFromRef;
    if (fieldCodeForCrf) {
      runSectionAttributesForCrf(fieldCodeForCrf);
      return () => { cancelled = true; };
    }

    const nCRFDtlNo = (defaultNCRFDtlNo != null && defaultNCRFDtlNo > 0 ? Number(defaultNCRFDtlNo) : null) || nCRFDtlNoFromRef;
    if (nCRFDtlNo == null) return;

    setCrfOptionsLoading(true);
    setCrfOptionsList([]);
    api.get('/action-items/section-attribute-by-crf-detail', { params: { nCRFDtlNo } })
      .then((res) => {
        if (cancelled) return;
        const item = res.data;
        const id = item?.id ?? item?.Id ?? item?.code ?? item?.Code;
        const vMedExCodeFromDetail = (id != null && String(id).trim() !== '') ? String(id).trim().split('_').pop() : null;
        if (vMedExCodeFromDetail) runSectionAttributesForCrf(vMedExCodeFromDetail);
        else setCrfOptionsLoading(false);
      })
      .catch(() => { if (!cancelled) { setCrfOptionsList([]); setCrfOptionsLoading(false); } });
    return () => { cancelled = true; };
  }, [isOpen, openedFromCRF, defaultProject, defaultVisitType, defaultSiteId, defaultVisitId, defaultNCRFDtlNo, defaultFieldCode, openPayloadRef, projectCode, visitType, siteId, visitId, currentProject]);

  // --- Conditional: opened from outside (Main grid) ---
  // Use section-attributes (full list), run only after visit is selected.
  useEffect(() => {
    if (openedFromCRF) return;
    if (!projectCode || !visitType) {
      setCrfOptionsList([]);
      return;
    }
    if (!visitId) {
      setCrfOptionsList([]);
      return;
    }

    let cancelled = false;
    setCrfOptionsLoading(true);
    setCrfOptionsList([]);
    const params = { projectCode, visitType, siteId: siteId || undefined, scheduledVisitId: visitId };
    api.get('/action-items/section-attributes', { params })
      .then((res) => {
        if (cancelled) return;
        const rawList = Array.isArray(res.data) ? res.data : [];
        const list = rawList.map((item) => {
          const id = item.id ?? item.Id ?? item.code ?? item.Code;
          const label = item.displayText ?? item.DisplayText ?? item.description ?? item.Description ?? item.code ?? item.Code ?? (id != null ? String(id) : '');
          const fieldType = (item.fieldType ?? item.FieldType ?? '').toString().trim();
          return { id: id != null ? String(id) : '', code: id != null ? String(id) : '', displayText: label, description: label, fieldType };
        }).filter((item) => item.id !== '');
        setCrfOptionsList(list);
      })
      .catch(() => { if (!cancelled) setCrfOptionsList([]); })
      .finally(() => { if (!cancelled) setCrfOptionsLoading(false); });
    return () => { cancelled = true; };
  }, [openedFromCRF, projectCode, visitType, siteId, visitId]);

  // When opened from CRF with default section attribute(s) or fieldCode, pre-select after options load (API id format: SubGroupCode_FieldCode; we may have full id or just fieldCode)
  useEffect(() => {
    if (!openedFromCRF || !crfOptionsList.length) return;
    const idsToSelect = crfDefaultsRef.current?.sectionAttributeIds ?? defaultSectionAttributeIds;
    const fieldCode = crfDefaultsRef.current?.fieldCode ?? defaultFieldCode;
    const matchId = (optionId, defaultId) => {
      if (optionId == null || defaultId == null) return false;
      const o = String(optionId).trim();
      const d = String(defaultId).trim();
      if (!o || !d) return false;
      const dLast = d.includes('_') ? d.split('_').pop() : d;
      const dShort = dLast.length <= 10 ? dLast : dLast.substring(0, 10);
      return (
        o === d ||
        o === dLast ||
        o.endsWith('_' + d) ||
        o.endsWith('_' + dLast) ||
        o.toLowerCase().endsWith('_' + d.toLowerCase()) ||
        o.toLowerCase().endsWith('_' + dLast.toLowerCase()) ||
        o.includes('_' + d) ||
        o.includes('_' + dLast) ||
        (dShort && (o.endsWith('_' + dShort) || o.toLowerCase().endsWith('_' + dShort.toLowerCase())))
      );
    };
    let toSelect = [];
    if (Array.isArray(idsToSelect) && idsToSelect.length > 0) {
      toSelect = crfOptionsList
        .map((item) => item.id || item.code)
        .filter((id) => id != null && idsToSelect.some((d) => matchId(id, d)))
        .map((id) => String(id));
    } else if (fieldCode && String(fieldCode).trim() !== '') {
      // Opened from CRF row with only fieldCode (MedEx): select the option that matches this field
      const f = String(fieldCode).trim();
      toSelect = crfOptionsList
        .filter((item) => matchId(item.id || item.code, f))
        .map((item) => String(item.id || item.code));
      if (toSelect.length === 0 && crfOptionsList.length === 1) toSelect = [String(crfOptionsList[0].id || crfOptionsList[0].code)];
    }
    if (toSelect.length > 0) {
      selectedSubgroupIdsRef.current = toSelect;
      setFormData((prev) => {
        const current = (prev.selectedSubgroupIds || []).filter(Boolean);
        const same = current.length === toSelect.length && toSelect.every((id, i) => current[i] === id);
        if (same) return prev;
        return { ...prev, selectedSubgroupIds: toSelect };
      });
    }
  }, [openedFromCRF, defaultSectionAttributeIds, defaultFieldCode, crfOptionsList]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name) setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'siteID') {
        next.visitType = (value || '').trim() ? (prev.visitType || '') : '';
      }
      if (name === 'visitType') {
        next.selectedSubgroupIds = [];
        selectedSubgroupIdsRef.current = [];
      }
      if (name === 'siteID' || name === 'visitType') {
        next.visitId = '';
      }
      if (name === 'issueCategory') {
        const desc = (value || '').toString().trim();
        const opt = (issueCategoryOptions || []).find(
          (o) => (o.vIssueCategoryDescription ?? o.VIssueCategoryDescription ?? '').toString().trim() === desc
        );
        const no = Number(opt?.nIssueCategoryMstNo ?? opt?.NIssueCategoryMstNo ?? 0);
        const isOthers = no === 10 || desc.toLowerCase() === 'others';
        if (!isOthers) next.issueCategoryOther = '';
      }
      return next;
    });
  };

  const handleSubgroupToggle = (id) => {
    const idStr = id != null ? String(id).trim() : '';
    if (!idStr) return;
    setFieldErrors((prev) => ({ ...prev, documentSections: '', sectionAttributes: '' }));
    setFormData((prev) => {
      const current = (prev.selectedSubgroupIds || []).map((s) => String(s).trim()).filter(Boolean);
      const set = new Set(current);
      if (set.has(idStr)) set.delete(idStr);
      else set.add(idStr);
      const next = Array.from(set);
      selectedSubgroupIdsRef.current = next;
      return { ...prev, selectedSubgroupIds: next };
    });
  };

  const validateForm = () => {
    const errs = {};
    if (!(formData.siteID || '').trim()) errs.siteID = actionItemMessages.siteRequired;
    if ((formData.visitType || '').trim() && !(formData.siteID || '').trim()) errs.visitType = actionItemMessages.visitTypeRequiresSite;
    if (Array.isArray(stageVisits) && stageVisits.length > 0 && !(formData.visitId || '').trim()) errs.visitId = actionItemMessages.visitRequired;
    if (!(formData.visitType || '').trim()) errs.visitType = actionItemMessages.visitTypeRequired;
    const selectedIds = (formData.selectedSubgroupIds || []).filter(Boolean);
    if (selectedIds.length === 0) errs.sectionAttributes = actionItemMessages.sectionAttributesRequired;
    else {
      const hasLabelSelected = selectedIds.some((id) => {
        const opt = crfOptionsList.find((o) => (o.id || o.code) === id);
        return opt && (opt.fieldType || '').toString().trim().toLowerCase() === 'label';
      });
      if (hasLabelSelected) {
        setToast({ type: 'error', message: 'Cannot Create Issue for Label field.' });
        errs._labelBlock = true;
      }
      // When opened from CRF with single question selected, nCRFDtlNo must be present to link action item
      if (openedFromCRF && selectedIds.length === 1) {
        const ncrfDtlNo = crfDefaultsRef.current?.nCRFDtlNo ?? defaultNCRFDtlNo;
        if (ncrfDtlNo == null || ncrfDtlNo <= 0) {
          errs.sectionAttributes = actionItemMessages.crfHeaderDetailRequired;
        }
      }
    }
    if (!(formData.actionItemDescription || '').trim()) errs.actionItemDescription = actionItemMessages.actionItemDescriptionRequired;
    if (!(formData.assignedTo || '').trim()) errs.assignedTo = actionItemMessages.assignedToRequired;
    if (!(formData.dueDate || '').trim()) errs.dueDate = actionItemMessages.dueDateRequired;
    else {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if ((formData.dueDate || '').trim() && formData.dueDate < todayStr) errs.dueDate = actionItemMessages.dueDateNoPast;
    }
    if (!(formData.dateOfAwareness || '').trim()) errs.dateOfAwareness = actionItemMessages.dateOfAwarenessRequired;
    else {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if ((formData.dateOfAwareness || '').trim() && formData.dateOfAwareness > todayStr) {
        errs.dateOfAwareness = actionItemMessages.dateOfAwarenessNoFuture;
      }
    }
    if (!(formData.dateOfIssue || '').trim()) errs.dateOfIssue = actionItemMessages.dateOfIssueRequired;
    else {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if (formData.dateOfIssue !== todayStr) {
        errs.dateOfIssue = actionItemMessages.dateOfIssueMustBeToday;
      }
    }
    if (!(formData.issueCategory || '').trim()) errs.issueCategory = actionItemMessages.issueCategoryRequired;
    if (isOthersIssueCategorySelection(formData.issueCategory, issueCategoryOptions) && !(formData.issueCategoryOther || '').trim()) {
      errs.issueCategoryOther = actionItemMessages.issueCategoryOtherRequired;
    }
    const pr = formData.nPriorityMstNo;
    if (pr === '' || pr == null || Number(pr) <= 0) errs.nPriorityMstNo = actionItemMessages.priorityRequired;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleClear = () => {
    setFieldErrors({});
    setSaveError(null);
    const todayStr = getLocalDateInputYmd();
    const craNo = getCraProfileNoFromAssignableProfiles(assignedToProfiles);
    setFormData((prev) => ({
      ...prev,
      actionItemDescription: '',
      assignTo: '',
      assignedTo: craNo || prev.assignedTo,
      dueDate: todayStr,
      dateOfAwareness: todayStr,
      dateOfIssue: todayStr,
      issueCategory: '',
      issueCategoryOther: '',
      nPriorityMstNo: '',
    }));
  };

  const handleSave = () => {
    if (visitReportViewOnly) return;
    setSaveError(null);
    if (!validateForm()) return;
    runAfterESign();
  };

  const handleClose = () => {
    onClose();
  };

  const runAfterESign = () => {
    const visitId = formData.visitId ? Number(formData.visitId) : 0;
    const description = (formData.actionItemDescription || '').trim();
    const selectedIds = (formData.selectedSubgroupIds || []).filter(Boolean);
    const refIds = (selectedSubgroupIdsRef.current || []).filter(Boolean);
    const idsToUse = selectedIds.length > 0 ? selectedIds : refIds;
    const section = 'Section Attributes';
    const singleNCRFDtlNo = openedFromCRF && idsToUse.length === 1
      ? (crfDefaultsRef.current?.nCRFDtlNo ?? defaultNCRFDtlNo)
      : null;
    const othersExtra = isOthersIssueCategorySelection(formData.issueCategory, issueCategoryOptions)
      ? { issueCategoryOther: (formData.issueCategoryOther || '').trim() || null }
      : {};
    const shared = {
      visitId,
      projectCode: formData.projectCode || currentProject || '',
      siteId: formData.siteID ? Number(formData.siteID) : 0,
      visitType: (formData.visitType || '').trim(),
      findingDescription: description,
      assignTo: (formData.assignTo || '').trim() || null,
      section,
      nPriorityMstNo: formData.nPriorityMstNo ? Number(formData.nPriorityMstNo) : 0,
      assignedTo: formData.assignedTo ? Number(formData.assignedTo) : 0,
      dueDate: formData.dueDate || null,
      dateOfAwareness: formData.dateOfAwareness || null,
      dateOfIssue: formData.dateOfIssue || null,
      issueCategory: (formData.issueCategory || '').trim() || null,
      ...othersExtra,
    };
    setSaving(true);
    // Sequential creates so each issue gets the next per-study AI-##### (parallel posts all read the same max before siblings commit).
    (async () => {
      const results = [];
      for (const id of idsToUse) {
        const payload = {
          ...shared,
          selectedAttributeId: String(id).trim(),
          nCRFDtlNo: singleNCRFDtlNo != null && singleNCRFDtlNo > 0 ? singleNCRFDtlNo : undefined,
        };
        try {
          await api.post('/action-items/create-from-visit', payload);
          results.push({ status: 'fulfilled' });
        } catch (reason) {
          results.push({ status: 'rejected', reason });
        }
      }

      const fulfilled = results.filter((r) => r.status === 'fulfilled');
      const rejected = results.filter((r) => r.status === 'rejected');
      const created = fulfilled.length;
      const failed = rejected.length;
      if (failed === 0) {
        const savePayload = {
          ...shared,
          /** When creating from a CRF question row, parent can highlight icons without waiting for GET /crf/page. */
          nCRFDtlNo: singleNCRFDtlNo != null && singleNCRFDtlNo > 0 ? Number(singleNCRFDtlNo) : undefined,
        };
        if (onSave) onSave(savePayload);
        setToast({
          type: 'success',
          message: created === 1
            ? actionItemMessages.savedSuccess
            : `${created} issues saved successfully.`,
        });
        onClose();
      } else {
        const firstError = rejected[0]?.reason?.response?.data?.error || rejected[0]?.reason?.message || 'Unknown error';
        setToast({
          type: 'error',
          message: created > 0 ? `${firstError} (${created} saved, ${failed} failed.)` : firstError,
        });
        setSaveError(null);
        if (created > 0 && onSave) {
          const savePayload = {
            ...shared,
            nCRFDtlNo: singleNCRFDtlNo != null && singleNCRFDtlNo > 0 ? Number(singleNCRFDtlNo) : undefined,
          };
          onSave(savePayload);
        }
      }
    })()
      .catch(() => {
        setToast({ type: 'error', message: 'Failed to create issues.' });
        setSaveError(null);
      })
      .finally(() => setSaving(false));
  };

  const showSectionAttributes = !!(formData.visitType || '').trim() && !!(projectCode || '').trim();

  const footer = visitReportViewOnly ? (
    <div className="create-action-item-footer">
      <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={saving}>
        Close
      </button>
    </div>
  ) : (
    <div className="create-action-item-footer">
      <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save'}
      </button>
      <button type="button" className="btn btn-outline-secondary" onClick={handleClear} disabled={saving}>
        Clear
      </button>
      <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={saving}>
        Close
      </button>
    </div>
  );

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const minDueDate = todayStr;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="create-action-item-title">
          Create New Issue
        </span>
      }
      size="large"
      className="create-action-item-modal"
      footer={footer}
      closeOnOverlayClick={false}
    >
      {loading ? (
        <Spinner />
      ) : (
        <>
          {error && <Alert type="error" message={error} />}

          <div className="create-action-item-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Project</label>
                <select
                  name="projectCode"
                  className="form-select"
                  value={formData.projectCode}
                  onChange={handleChange}
                  disabled
                >
                  <option value="">Select Project</option>
                  {projects.map((p) => (
                    <option key={p.projectCode} value={p.projectCode}>
                      {p.projectCode} {p.projectDescription ? `- ${p.projectDescription}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Site <span className="required-asterisk">*</span></label>
                <select
                  name="siteID"
                  className="form-select"
                  value={formData.siteID}
                  onChange={handleChange}
                  disabled={visitReportViewOnly || openedFromCRF}
                >
                  <option value="">Select Site</option>
                  {sites.map((s) => (
                    <option key={s.siteID} value={String(s.siteID)}>
                      {s.siteName}
                    </option>
                  ))}
                </select>
                {fieldErrors.siteID && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.siteID}</div>}
              </div>
            </div>

            <div className="form-row">
              
              <div className="form-group">
                <label className="form-label">Visit Type <span className="required-asterisk">*</span></label>
                <select
                  name="visitType"
                  className="form-select"
                  value={formData.visitType}
                  onChange={handleChange}
                  disabled={visitReportViewOnly || openedFromCRF || !siteId}
                >
                  <option value="">Select visit type</option>
                  <option value="SSV">Site Selection Visit</option>
                  <option value="SIV">Site Initiation Visit</option>
                  <option value="SMV">Site Monitoring Visit</option>
                  <option value="SCV">Site Close-Out Visit</option>
                </select>
                {fieldErrors.visitType && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.visitType}</div>}
              </div>

              {Array.isArray(stageVisits) && (
              <div className="form-group">
                <label className="form-label">Visit <span className="required-asterisk">*</span></label>
                {stageVisits.length === 0 ? (
                  <p className="form-text text-muted">No visits in current stage. Create issues when visits are in In Progress, First Review or Second Review.</p>
                ) : (
                  <select
                    name="visitId"
                    className="form-select"
                    value={formData.visitId}
                    onChange={handleChange}
                    disabled={visitReportViewOnly || openedFromCRF || !siteId}
                  >
                    <option value="">Select visit</option>
                    {visitOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
                {fieldErrors.visitId && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.visitId}</div>}
              </div>
            )}
            </div>

            

            {showSectionAttributes && (
              <div className="form-group create-action-item-subgroup-section">
                <label className="form-label">Field Name <span className="required-asterisk">*</span></label>
                {crfOptionsLoading ? (
                  <div className="create-action-item-checkbox-list">
                    <span className="create-action-item-no-subgroups">Loading...</span>
                  </div>
                ) : (
                  <div className="create-action-item-checkbox-list">
                    {crfOptionsList.map((item) => {
                      const id = item.id || item.code;
                      if (id == null || id === '') return null;
                      const idStr = String(id);
                      const label = item.description ?? item.displayText ?? item.code ?? idStr;
                      const selectedIds = (formData.selectedSubgroupIds || []).map((s) => String(s));
                      const isChecked = selectedIds.includes(idStr);
                      return (
                        <label key={idStr} className="create-action-item-checkbox-item">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleSubgroupToggle(idStr)}
                            disabled={visitReportViewOnly || openedFromCRF}
                            value={idStr}
                          />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                    {crfOptionsList.length === 0 && !crfOptionsLoading && (
                      <span className="create-action-item-no-subgroups">
                        No options found. Ensure a CRF template is assigned to this project/site and visit type (Map Template to Visit).
                      </span>
                    )}
                  </div>
                )}
                {fieldErrors.sectionAttributes && (
                  <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
                    {fieldErrors.sectionAttributes}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Issue Description <span className="required-asterisk">*</span></label>
              <textarea
                name="actionItemDescription"
                className="form-input form-textarea"
                placeholder="Describe the issue"
                value={formData.actionItemDescription}
                onChange={handleChange}
                rows={4}
                disabled={visitReportViewOnly}
              />
              {fieldErrors.actionItemDescription && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.actionItemDescription}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Assign To</label>
              <input
                type="text"
                name="assignTo"
                className="form-input"
                placeholder="Enter Assigned To"
                value={formData.assignTo}
                onChange={handleChange}
                disabled={visitReportViewOnly}
                maxLength={500}
                autoComplete="off"
              />
            </div>

            <div className="form-row">
              <div className="form-group" style={{ display: 'none' }}>
                <label className="form-label">Assigned To <span className="required-asterisk">*</span></label>
                <select
                  name="assignedTo"
                  className="form-select"
                  value={formData.assignedTo}
                  onChange={handleChange}
                  disabled
                  title="New issues are assigned to CRA by default."
                >
                  {!formData.assignedTo ? <option value="">Select Assigned To</option> : null}
                  {assignedToProfiles.map((p) => {
                    const id = p.profileNo ?? p.nProfileNo;
                    const description = (p.profileDescription ?? p.ProfileDescription ?? '').toString().trim();
                    const name = description || (p.profileCode ?? p.ProfileCode) || `Profile ${id}`;
                    return (
                      <option key={id ?? (p.profileCode ?? p.ProfileCode)} value={id ?? ''}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                {fieldErrors.assignedTo && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.assignedTo}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Date of Awareness <span className="required-asterisk">*</span></label>
                <input
                  type="date"
                  name="dateOfAwareness"
                  className="form-input"
                  value={formData.dateOfAwareness}
                  max={todayStr}
                  onChange={handleChange}
                  disabled={visitReportViewOnly}
                />
                {fieldErrors.dateOfAwareness && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.dateOfAwareness}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Date of Issue Creation<span className="required-asterisk">*</span></label>
                <input
                  type="date"
                  name="dateOfIssue"
                  className="form-input"
                  value={formData.dateOfIssue}
                  min={todayStr}
                  max={todayStr}
                  disabled
                />
                {fieldErrors.dateOfIssue && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.dateOfIssue}</div>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Expected Closure Date <span className="required-asterisk">*</span></label>
                <input
                  type="date"
                  name="dueDate"
                  className="form-input"
                  value={formData.dueDate}
                  min={minDueDate}
                  onChange={handleChange}
                  disabled={visitReportViewOnly}
                />
                {fieldErrors.dueDate && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.dueDate}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Issue Category <span className="required-asterisk">*</span></label>
                <select
                  name="issueCategory"
                  className="form-select"
                  value={formData.issueCategory}
                  onChange={handleChange}
                  disabled={visitReportViewOnly}
                >
                  <option value="">Select issue category</option>
                  {issueCategoryOptions.map((opt) => {
                    const desc = (opt.vIssueCategoryDescription ?? opt.VIssueCategoryDescription ?? '').toString();
                    const key = opt.nIssueCategoryMstNo ?? opt.NIssueCategoryMstNo ?? desc;
                    return (
                      <option key={key} value={desc}>
                        {desc}
                      </option>
                    );
                  })}
                </select>
                {fieldErrors.issueCategory && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.issueCategory}</div>}
              </div>
            </div>
            <div className="form-row">
              {isOthersIssueCategorySelection(formData.issueCategory, issueCategoryOptions) && (
                <div className="form-group">
                  <label className="form-label">Specify Issue Category <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    name="issueCategoryOther"
                    className="form-input"
                    value={formData.issueCategoryOther}
                    onChange={handleChange}
                    disabled={visitReportViewOnly}
                    placeholder="Enter Issue Category"
                    maxLength={500}
                    autoComplete="off"
                  />
                  {fieldErrors.issueCategoryOther && (
                    <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.issueCategoryOther}</div>
                  )}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Priority <span className="required-asterisk">*</span></label>
                <select
                  name="nPriorityMstNo"
                  className="form-select"
                  value={formData.nPriorityMstNo}
                  onChange={handleChange}
                  disabled={visitReportViewOnly}
                >
                  <option value="">Select priority</option>
                  {priorityOptions.map((opt) => {
                    const id = opt.nPriorityMstNo ?? opt.NPriorityMstNo;
                    const label = (opt.vPriorityDescription ?? opt.VPriorityDescription ?? '').toString();
                    const hours = opt.nHours ?? opt.NHours;
                    return (
                      <option key={id} value={id}>
                        {label}{hours != null ? ` (${hours}h)` : ''}
                      </option>
                    );
                  })}
                </select>
                {fieldErrors.nPriorityMstNo && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.nPriorityMstNo}</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    </>
  );
};

export default CreateActionItemModal;
